import React, { useState, useEffect } from 'react';
import { ChevronRight, Shield, Database, Share2, Lock, Users, FileText, Mail, Globe, Calendar } from 'lucide-react';

import './DocMatePrivacyPolicy.css';

const DocMatePrivacyPolicy = () => {
    const [activeSection, setActiveSection] = useState('introduction');

    const sections = [
        { id: 'introduction', title: 'Introduction', icon: Shield },
        { id: 'data-collection', title: 'Data We Collect', icon: Database },
        { id: 'data-usage', title: 'How We Use Your Data', icon: FileText },
        { id: 'data-sharing', title: 'Data Sharing', icon: Share2 },
        { id: 'data-storage', title: 'Data Storage & Security', icon: Lock },
        { id: 'your-rights', title: 'Your Rights', icon: Users },
        { id: 'permissions', title: 'Permissions', icon: Shield },
        { id: 'age-restriction', title: 'Age Restriction', icon: Users },
        { id: 'policy-updates', title: 'Policy Updates', icon: Calendar },
        { id: 'contact', title: 'Contact Us', icon: Mail },
        { id: 'applicable-law', title: 'Applicable Law', icon: Globe }
    ];

    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = window.scrollY + 100;

            for (let i = sections.length - 1; i >= 0; i--) {
                const element = document.getElementById(sections[i].id);
                if (element && element.offsetTop <= scrollPosition) {
                    setActiveSection(sections[i].id);
                    break;
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToSection = (sectionId) => {
        setActiveSection(sectionId);
        const element = document.getElementById(sectionId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <>
            <div className="privacy-container">
                <div className="sidebar">
                    <div className="sidebar-content">
                        <div className="sidebar-header">
                            <div className="logo">
                                <Shield size={20} />
                            </div>
                            <div>
                                <h3 style={{fontSize: '26px'}}>DocMate</h3>
                                <p style={{fontSize: '22px', color: '#666'}}>Privacy Policy</p>
                            </div>
                        </div>

                        <nav>
                            {sections.map((section) => {
                                const Icon = section.icon;
                                return (
                                    <button
                                        key={section.id}
                                        onClick={() => scrollToSection(section.id)}
                                        className={`nav-button ${activeSection === section.id ? 'active' : ''}`}
                                    >
                                        <Icon size={16} />
                                        <span style={{fontSize: '20px', fontWeight: '500'}}>{section.title}</span>
                                        {activeSection === section.id && <ChevronRight size={16} style={{marginLeft: 'auto'}} />}
                                    </button>
                                );
                            })}
                        </nav>
                    </div>
                </div>

                <div className="main-content">
                    <div style={{marginBottom: '32px'}}>
                        <h1>DocMate Privacy Policy</h1>
                        <p><strong>Effective Date: June 23, 2025</strong></p>
                    </div>

                    <section id="introduction" className="section">
                        <h2>
                            <Shield className="section-icon" size={24} />
                            Introduction
                        </h2>
                        <p>
                            DocMate is a Chrome extension that makes your Google Docs experience better by creating tables, graphs, lists, and grammar corrections based on text you select or prompts you provide. It uses OpenAI’s technology to process your requests. This Privacy Policy explains how we handle your data during our beta testing phase. As we’re still in beta, this policy may change, and we’ll let you know through in-app notifications or by email.
                        </p>
                    </section>

                    <section id="data-collection" className="section">
                        <h2>
                            <Database className="section-icon" size={24} />
                            1. Data We Collect
                        </h2>
                        <p>We collect only the data needed to make DocMate work and improve it:</p>

                        <div className="info-box">
                            <h3>Account Information</h3>
                            <p>When you sign up, we collect your email address, full name, and password.</p>
                        </div>

                        <div className="info-box">
                            <h3>Your Inputs</h3>
                            <p>We use the text you select in Google Docs or the prompts you type to create tables, graphs, lists, or grammar corrections. We don’t save these inputs after processing them.</p>
                        </div>

                        <div className="info-box">
                            <h3>Document Formatting</h3>
                            <p>We look at the font size and type of your selected text to match your Google Docs style, but we don’t save this information.</p>
                        </div>

                        <div className="info-box">
                            <h3>Browser Storage</h3>
                            <p>We store your email, full name, and a login token in your browser to keep you signed in for up to 7 days. We also store a secure refresh token on our server to manage your session.</p>
                        </div>

                        <div className="info-box">
                            <h3>Usage Information</h3>
                            <p>We use basic Chrome Web Store analytics (like crash reports and usage stats) to check how the extension is working.</p>
                        </div>
                    </section>

                    <section id="data-usage" className="section">
                        <h2>
                            <FileText className="section-icon" size={24} />
                            2. How We Use Your Data
                        </h2>
                        <p>We use your data to:</p>
                        <div className="highlight-box">
                            <ul className="bullet-list">
                                <li>
                                    <span className="bullet-point"></span>
                                    Set up and manage your DocMate account.
                                </li>
                                <li>
                                    <span className="bullet-point"></span>
                                    Process your selected text or prompts to create tables, graphs, lists, or grammar corrections using OpenAI’s technology.
                                </li>
                                <li>
                                    <span className="bullet-point"></span>
                                    Match the style of our responses to your Google Docs document.
                                </li>
                                <li>
                                    <span className="bullet-point"></span>
                                    Keep you signed in securely.
                                </li>
                                <li>
                                    <span className="bullet-point"></span>
                                    Monitor how the extension performs to fix issues and improve it.
                                </li>
                            </ul>
                        </div>
                    </section>

                    <section id="data-sharing" className="section">
                        <h2>
                            <Share2 className="section-icon" size={24}/>
                            3. Data Sharing
                        </h2>
                        <p>We don’t sell or share your personal information for marketing. We only share data when necessary:</p>

                        <div className="openai-box">
                            <h3>OpenAI</h3>
                            <p>We send your selected text or prompts to OpenAI to generate responses. We don’t share your personal details (like email or name). OpenAI’s data handling follows their <a href="https://openai.com/policies/row-privacy-policy/">Privacy Policy</a>.</p>
                        </div>

                        <div className="service-box">
                            <h3>Our Hosting Provider</h3>
                            <p>Your account information is stored securely in a database on Google Cloud Platform, which uses strong security standards.</p>
                        </div>

                        <div className="legal-box">
                            <h3>Legal Needs</h3>
                            <p>We may share data if required by law or to protect our rights.</p>
                        </div>
                    </section>

                    <section id="data-storage" className="section">
                        <h2>
                            <Lock className="section-icon" size={24}/>
                            4. Data Storage and Security
                        </h2>
                        <div className="grid-2">
                            <div className="info-box">
                                <h3>Storage</h3>
                                <p>Your account information (email, full name, and hashed password) is stored securely in our database on Google Cloud Platform. Your password is protected with encryption. Your email, full name, and login token are stored in your browser for up to 7 days to keep you signed in. A secure refresh token is stored on our server.</p>
                            </div>
                            <div className="info-box">
                                <h3>Security</h3>
                                <p>We use strong security measures to protect your data, like encrypting passwords and securing login tokens. Data stored in your browser isn’t encrypted, so we recommend signing out when you’re done to clear it.</p>
                            </div>
                        </div>
                        <div className="highlight-box" style={{marginTop: '16px'}}>
                            <p><strong>Future Plans:</strong> After beta, we’ll add options for you to delete your account and data.</p>
                        </div>
                    </section>

                    <section id="your-rights" className="section" style={{ fontSize: '1.2rem' }}>
                        <h2 style={{ fontSize: '1.8rem' }}>
                            <Users className="section-icon" size={24} />
                            5. Your Rights
                        </h2>
                        <div className="warning-box" style={{ fontSize: '1.2rem' }}>
                            <p>
                                During beta testing, you can’t directly view, edit, or delete your data through the extension. If you want to delete your account or have questions, email us at <a href="mailto:CyberneticBuilds@gmail.com">CyberneticBuilds@gmail.com</a>, and we’ll help you. We plan to add easier data management options after beta.
                            </p>
                        </div>
                    </section>

                    <section id="permissions" className="section">
                        <h2>
                            <Shield className="section-icon" size={24} />
                            6. Permissions
                        </h2>
                        <p>
                            DocMate needs a few permissions to work, as listed in our Chrome extension settings:
                        </p>
                        <div className="highlight-box">
                            <ul className="bullet-list">
                                <li>
                                    <span className="bullet-point"></span>
                                    Clipboard Access: To read the text you select in Google Docs and insert our responses (like tables or graphs).
                                </li>
                                <li>
                                    <span className="bullet-point"></span>
                                    Browser Storage: To save your email, full name, and login token to keep you signed in.
                                </li>
                                <li>
                                    <span className="bullet-point"></span>
                                    Access to Google Docs: To work within Google Docs pages.
                                </li>
                                <li>
                                    <span className="bullet-point"></span>
                                    Connection to Our Server: To process your requests securely through our API (https://api.aidex.co).
                                </li>
                            </ul>
                            <p>We only use these permissions to provide DocMate’s features.</p>
                        </div>
                    </section>

                    <section id="age-restriction" className="section">
                        <h2>
                            <Users className="section-icon" size={24} />
                            7. Age Restriction
                        </h2>
                        <div className="danger-box">
                            <p>
                                DocMate is not for users under 16. If you’re under 16, please get your parent’s permission before using the extension.
                            </p>
                        </div>
                    </section>

                    <section id="policy-updates" className="section">
                        <h2>
                            <Calendar className="section-icon" size={24} />
                            8. Policy Updates
                        </h2>
                        <p>
                            We may update this Privacy Policy as DocMate improves. We’ll notify you of changes through in-app notifications or by email at <a href="mailto:CyberneticBuilds@gmail.com">CyberneticBuilds@gmail.com</a>.
                        </p>
                    </section>

                    <section id="contact" className="section">
                        <h2>
                            <Mail className="section-icon" size={24} />
                            9. Contact Us
                        </h2>
                        <div className="contact-box">
                            <p>For questions or concerns about this Privacy Policy, reach out to us at:</p>
                            <p><strong>Email: <a href="mailto:CyberneticBuilds@gmail.com">CyberneticBuilds@gmail.com</a></strong></p>
                        </div>
                    </section>

                    <section id="applicable-law" className="section">
                        <h2>
                            <Globe className="section-icon" size={24} />
                            10. Applicable Law
                        </h2>
                        <p>
                            This Privacy Policy follows data protection laws to keep your information safe. As we grow, we may update it to meet additional legal requirements.
                        </p>
                    </section>
                </div>
            </div>
        </>
    );
};

export default DocMatePrivacyPolicy;